'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { parseHttpRequest } from '@terra/shared-utils';
import type { StoredHarDisplayEntry } from '@terra/shared-utils';
import { toasterApi } from '../../toast/ToastProvider';
import { resolveReplayUrl } from './http-replay-url.utils';

/**
 * Data structure for HTTP request replay
 */
export type HttpReplayData = {
  method: string;
  url: string;
  headers?: Record<string, string | string[]>;
  body?: string;
  bodyEncoding?: 'utf8' | 'base64';
  httpVersion?: string;
  harEntry?: StoredHarDisplayEntry;
};

export type SendRawToReplayOptions = {
  harEntry?: StoredHarDisplayEntry;
};

type HttpReplayContextType = {
  /**
   * Send an HTTP request to the replay tab for editing and resending.
   * Returns true if the request was successfully sent.
   */
  sendToReplay: (data: HttpReplayData) => void;
  /**
   * Send raw HTTP content (in raw HTTP format) to the replay tab.
   * The content will be parsed to extract method, url, headers, and body.
   */
  sendRawToReplay: (content: string, options?: SendRawToReplayOptions) => void;
  /** Whether the replay feature is available */
  isAvailable: boolean;
};

const HttpReplayContext = createContext<HttpReplayContextType | undefined>(
  undefined,
);

type HttpReplayProviderProps = {
  children: ReactNode;
  /**
   * Callback to handle sending HTTP request data to the replay tab.
   * If not provided, the replay feature will be marked as unavailable.
   */
  onSendToReplay?: (data: HttpReplayData) => void;
};

export function HttpReplayProvider({
  children,
  onSendToReplay,
}: HttpReplayProviderProps) {
  const contextValue: HttpReplayContextType = useMemo(
    () => ({
      sendToReplay: (data: HttpReplayData) => {
        if (onSendToReplay) {
          onSendToReplay(data);
        }
      },
      sendRawToReplay: (content: string, options?: SendRawToReplayOptions) => {
        if (onSendToReplay) {
          const parsed = parseHttpRequest(content);

          if (parsed) {
            onSendToReplay({
              method: parsed.method,
              url: resolveReplayUrl({
                url: parsed.url,
                headers: parsed.headers,
              }),
              headers: parsed.headers,
              body: parsed.body.trim() ? parsed.body : undefined,
              httpVersion: parsed.httpVersion,
              ...(options?.harEntry ? { harEntry: options.harEntry } : {}),
            });

            return;
          }

          toasterApi.error({
            title: 'Failed to send to replay',
            description:
              'Could not parse the request. Check the request format.',
          });
        }
      },
      isAvailable: !!onSendToReplay,
    }),
    [onSendToReplay],
  );

  return (
    <HttpReplayContext.Provider value={contextValue}>
      {children}
    </HttpReplayContext.Provider>
  );
}

/**
 * Hook to access the HTTP replay context.
 * Returns undefined if not within a provider.
 */
export function useHttpReplayContext(): HttpReplayContextType | undefined {
  return useContext(HttpReplayContext);
}

/**
 * Hook to get the sendRawToReplay function if available.
 * Returns undefined if not within a provider or replay is not available.
 */
export function useSendToReplay():
  | ((content: string, options?: SendRawToReplayOptions) => void)
  | undefined {
  const context = useContext(HttpReplayContext);

  if (!context?.isAvailable) return undefined;

  return context.sendRawToReplay;
}
